import type { SurveyCreateDto, SurveyUseCreateDto } from '@@types/api.types';
import { AssessmentType } from '@@types/api.types';
import { zRequiredUuid } from '@utils/zod.util';
import { z } from 'zod';

/**
 * Schema for validating survey use creation data.
 * Ensures that each use has a valid UUID for both useId and domainId.
 */
const surveyUseCreateSchema = z
  .object({
    domainId: zRequiredUuid('Please select a Domain'),
    useId: zRequiredUuid('Please select an Use'),
  })
  .strip() satisfies z.ZodType<SurveyUseCreateDto>;

/**
 * Schema for validating survey creation data.
 * Includes UI-specific fields for validation but transforms to clean API output.
 */
export const surveyCreateSchema = z
  .object({
    assessmentType: z.nativeEnum(AssessmentType, {
      errorMap: () => ({ message: 'Please select an assessment type' }),
    }),
    selectedDomains: z.record(z.string(), z.array(z.string())).optional(),
    selectedUses: z.array(z.string()).optional(),
    technologyId: zRequiredUuid('Technology ID is required'),
    uses: z.array(surveyUseCreateSchema).optional(),
  })
  .superRefine((data, ctx) => {
    if (!data.selectedUses || data.selectedUses.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one Use must be selected',
        path: ['selectedUses'],
      });
      return;
    }

    // Validate UI state: each selected use must have at least one domain
    if (data.selectedUses && data.selectedDomains) {
      data.selectedUses.forEach((useId) => {
        const domains = data.selectedDomains?.[useId] || [];
        if (domains.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'At least one Domain must be selected for each selected Use',
            path: ['selectedDomains', useId],
          });
        }
      });
    }
  })
  .transform((data) => ({
    assessmentType: data.assessmentType,
    technologyId: data.technologyId,
    uses: data.uses,
  })) satisfies z.ZodType<SurveyCreateDto, z.ZodTypeDef, SurveyCreateForm>;

type SurveyCreateForm = SurveyCreateDto & { selectedUses?: string[]; selectedDomains?: Record<string, string[]> };

/**
 * Form data type for survey creation form.
 */
export type SurveyCreateFormData = z.infer<typeof surveyCreateSchema>;

/**
 * Form errors type for survey creation form validation.
 */
export type SurveyCreateFormErrors = z.inferFormattedError<z.ZodType<SurveyCreateForm>, string>;
